import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService, { functionalities } from '@/shared/services/AuthService';

import RegistrationGroupComponent from './RegistrationGroupComponent';
import { registrationShape, actions } from './registrationHelpers';
import {
  createEmptyGroup,
  createGroupFromRegistrations,
  groupHasExisting,
  applySharedAction,
  applyPartAction,
  buildGroupPayload,
  isGroupValid,
  groupRepresentative,
} from './registrationGroupHelpers';
import {
  createOrUpdateInteraction,
  linkInteractionAttachments,
  getInteractionForRegistration,
} from './resourceApi';
import messages from './registrationMessages';

/**
 * Modal wrapper for the grouped registration form. Owns the `{ work, cost, travel }` state,
 * applies shared/part changes through the group reducer, and saves the filled parts as one
 * `[work, cost, travel]` array — the same endpoint the single registration uses.
 */
class RegistrationGroupModal extends Component {
  state = {
    group: null,
    showValidation: false,
    priceLoading: false,
    attachments: [],
  };

  componentDidMount() {
    const { registration, registrations } = this.props;
    const editRegs =
      registrations && registrations.length
        ? registrations
        : (registration && registration.id && [registration]) || null;
    const group = editRegs
      ? createGroupFromRegistrations(editRegs)
      : createEmptyGroup(registration || {});
    this.setState({ group });

    // Load the existing communication report so it shows when editing the group.
    const groupId = editRegs && editRegs[0].group;
    if (groupId) {
      getInteractionForRegistration(groupId).then(interaction => {
        if (interaction) this.onSharedAction(actions.interactionInit, interaction);
      });
    }
  }

  onSharedAction = (action, value) =>
    this.setState(({ group }) => ({ group: applySharedAction(group, action, value) }));

  onPartAction = (part, action, value) =>
    this.setState(({ group }) => ({ group: applyPartAction(group, part, action, value) }));

  isFormInvalid = () => {
    const { group } = this.state;
    const { registrationOptions } = this.props;
    return !isGroupValid(group, registrationOptions);
  };

  showValidation = () => this.setState({ showValidation: true });

  hasSavePermission = () => {
    const { group } = this.state;
    if (!group) return false;
    const rep = groupRepresentative(group);
    if (rep.userId && rep.userId !== AuthService.getUserId())
      return AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: 'Update',
      });
    return AuthService.hasPermission({
      functionality: functionalities.RegistrationOwn,
      privilege: 'Update',
    });
  };

  saveGroup = () => {
    if (this.isFormInvalid()) return undefined;
    const { intl, onSave } = this.props;
    const { group } = this.state;

    const payload = buildGroupPayload(group);
    if (!payload.length) return undefined;
    const { attachments } = this.state;

    const isEdit = groupHasExisting(group);
    const request = isEdit
      ? api.base.put(`${config.appUrls.api}/api/Registration/PUT`, payload)
      : api.base.post(`${config.appUrls.api}/api/Registration/POST`, payload);

    return request.then(
      ({ data }) => {
        const saved = data || [];
        if (!saved.length) return;
        ToastrService.success(
          intl.formatMessage(isEdit ? messages.RegistrationModified : messages.Registered),
        );
        const rep = groupRepresentative(group);
        createOrUpdateInteraction(rep, saved[0].group)
          .then(response => {
            const interactionId = rep.interactionId || response?.data?.id;
            if (interactionId) {
              return linkInteractionAttachments(interactionId, rep.relation?.id, attachments);
            }
            return undefined;
          })
          .then(() => onSave(saved));
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  render() {
    const { intl, show, onClose } = this.props;
    const { group, showValidation, priceLoading } = this.state;
    const hasPermission = this.hasSavePermission();
    const hasRelation = !!(group && groupRepresentative(group).relation?.id);
    const isEdit = !!group && groupHasExisting(group);

    return (
      <OkCancelModal
        title={intl.formatMessage(isEdit ? messages.EditRegistration : messages.NewRegistration)}
        show={show}
        onClose={onClose}
        size="lg"
        customClassName="edit-registration-modal"
        dismissOnClickOutsideModal
        zendeskArticle="5434903907730"
        disabled={
          !hasPermission || !hasRelation || priceLoading || (showValidation && this.isFormInvalid())
        }
        onSave={this.saveGroup}
        onHoverSave={this.showValidation}
        cancelLabel={
          <>
            <i className="glyphicon glyphicon-remove margin5R" />
            {intl.formatMessage(messages.Cancel)}
          </>
        }
        okLabel={
          <>
            <i className="glyphicon glyphicon-floppy-disk margin5R" />
            {intl.formatMessage(messages.Register)}
          </>
        }
      >
        <div className="form-horizontal">
          {group ? (
            <RegistrationGroupComponent
              group={group}
              onSharedAction={this.onSharedAction}
              onPartAction={this.onPartAction}
              onPriceLoadingChanged={loading => this.setState({ priceLoading: loading })}
              options={this.props.registrationOptions}
              showValidation={showValidation}
              attachments={this.state.attachments}
              onAttachmentsChange={attachments => this.setState({ attachments })}
              intl={intl}
            />
          ) : null}
        </div>
      </OkCancelModal>
    );
  }
}

RegistrationGroupModal.propTypes = {
  registration: registrationShape,
  registrations: PropTypes.arrayOf(PropTypes.shape({})),
  onSave: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  registrationOptions: PropTypes.shape({}),
};

RegistrationGroupModal.defaultProps = {
  show: false,
  registration: null,
  registrations: null,
  registrationOptions: {},
};

export default injectIntl(RegistrationGroupModal);
